import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate} from "react-router-dom";
import Cookies from "js-cookie";
import Config from "../../core/config";
import {mergeAuthHeaders} from "../../utils/httpRequest";
import {ensureSessionIsActive, logoutAndRedirect, renewActiveSession} from "../../utils/session";
import {getCompanyByUserUUID} from "../../utils/requestUtils";

const apiUrl = Config.apiUrl;

/** Largura máxima do cartão / QR (celular usa quase a tela inteira). */
const QR_MODAL_MAX_PX = 500;

/** Maiúsculas: letras (incluindo acentos), números, espaço, hífen e apóstrofo. */
function sanitizeQrUserNameInput(value) {
    const upper = value.toUpperCase();
    return [...upper]
        .filter((ch) => /[A-ZÀ-ÖØ-Ý0-9]/.test(ch) || /\s/.test(ch) || ch === '-' || ch === "'")
        .join('');
}

const Shortcuts = () => {
    const navigate = useNavigate();
    const [deliveryEnabled, setDeliveryEnabled] = useState(false);
    const [showTableOrder, setShowTableOrder] = useState(true);
    const [showComandaOrder, setShowComandaOrder] = useState(false);
    const [showTakeAwayOrder, setShowTakeAwayOrder] = useState(true);
    const [showCounterOrder, setShowCounterOrder] = useState(true);
    const [showWaiterQrCode, setShowWaiterQrCode] = useState(true);
    const [qrModalOpen, setQrModalOpen] = useState(false);
    const [qrInviteName, setQrInviteName] = useState('');
    const [qrInviteUserType, setQrInviteUserType] = useState(0);
    const [qrLoading, setQrLoading] = useState(false);
    const [qrError, setQrError] = useState(null);
    const [qrResult, setQrResult] = useState(null);

    // Verifica o tipo de usuário
    const userCookies = Cookies.get('user');
    let userType = 0; // BASIC por padrão
    if (userCookies) {
        try {
            const user = JSON.parse(userCookies);
            userType = user.userType !== undefined ? user.userType : 0;
        } catch (error) {
            console.error('Erro ao ler dados do usuário:', error);
        }
    }

    // Constantes de tipo de usuário (deve corresponder ao backend)
    const BASIC = 0;
    const INTERMEDIATE = 1;
    const ADMIN = 2;

    const isBasicUser = userType === BASIC;
    const isIntermediateUser = userType === INTERMEDIATE;
    const isAdminUser = userType === ADMIN;
    const qrUserTypeOptions = [
        {value: BASIC, label: 'BÁSICO'},
        {value: INTERMEDIATE, label: 'INTERMEDIÁRIO'},
        {value: ADMIN, label: 'AVANÇADO'},
    ];
    const allowedQrUserTypeOptions = qrUserTypeOptions.filter((opt) => opt.value <= userType);

    const openQrModal = () => {
        setQrInviteName('');
        setQrInviteUserType(allowedQrUserTypeOptions[0]?.value ?? BASIC);
        setQrError(null);
        setQrResult(null);
        setQrModalOpen(true);
    };

    const closeQrModal = useCallback(() => {
        setQrModalOpen(false);
        setQrLoading(false);
        setQrResult(null);
        setQrError(null);
    }, []);

    useEffect(() => {
        getCompanyByUserUUID().then((company) => {
            if (company) {
                setDeliveryEnabled(!!company.deliveryEnabled);
                setShowTableOrder(company.showTableOrder !== false);
                setShowComandaOrder(!!company.showComandaOrder);
                setShowTakeAwayOrder(company.showTakeAwayOrder !== false);
                setShowCounterOrder(company.showCounterOrder !== false);
                setShowWaiterQrCode(company.showWaiterQrCode !== false);
            }
        });
    }, []);

    const navigateWithSessionCheck = useCallback((path) => {
        if (!ensureSessionIsActive()) {
            logoutAndRedirect();
            return;
        }
        renewActiveSession();
        navigate(path);
    }, [navigate]);

    useEffect(() => {
        if (!qrModalOpen || !qrResult?.loginUrl) {
            return undefined;
        }
        let hash;
        try {
            hash = new URL(qrResult.loginUrl).searchParams.get('hash');
        } catch {
            return undefined;
        }
        if (!hash) {
            return undefined;
        }
        const poll = async () => {
            try {
                const response = await fetch(
                    `${apiUrl}/api/users/qr-login-invite-status?hash=${encodeURIComponent(hash)}`,
                    {headers: mergeAuthHeaders({})}
                );
                if (response.status === 401 || response.status === 403) {
                    logoutAndRedirect();
                    return;
                }
                if (!response.ok) {
                    return;
                }
                const data = await response.json();
                renewActiveSession();
                if (data.consumed) {
                    closeQrModal();
                }
            } catch {
                /* ignora falhas pontuais entre um poll e outro */
            }
        };
        const id = setInterval(poll, 2000);
        poll();
        return () => clearInterval(id);
    }, [qrModalOpen, qrResult, closeQrModal]);

    const submitQrInvite = async () => {
        const name = (qrInviteName || '').trim();
        if (!name) {
            setQrError('Informe o nome do novo usuário.');
            return;
        }
        if (!allowedQrUserTypeOptions.some((opt) => opt.value === qrInviteUserType)) {
            setQrError('Tipo de usuário inválido para o seu nível de acesso.');
            return;
        }
        setQrLoading(true);
        setQrError(null);
        try {
            const response = await fetch(`${apiUrl}/api/users/qr-login-invite`, {
                method: 'POST',
                headers: mergeAuthHeaders({'Content-Type': 'application/json'}),
                body: JSON.stringify({
                    name,
                    userType: qrInviteUserType,
                    siteBaseUrl: window.location.origin,
                }),
            });
            if (response.status === 401 || response.status === 403) {
                logoutAndRedirect();
                return;
            }
            if (!response.ok) {
                const msg = await response.text();
                setQrError(msg || 'Não foi possível gerar o convite.');
                return;
            }
            const data = await response.json();
            renewActiveSession();
            setQrResult(data);
        } catch (e) {
            console.error(e);
            setQrError('Erro de rede ao gerar o convite.');
        } finally {
            setQrLoading(false);
        }
    };

    return (
        <div className="shortcuts-grid">
            {/* Pedido Mesa - visível para todos, se habilitado nos parâmetros da empresa */}
            {showTableOrder && (
                <button className={"shorcut-button"} onClick={() => {
                    localStorage.removeItem('sale');
                    navigateWithSessionCheck('/sale/table');
                }}>Pedido Mesa
                </button>
            )}

            {/* Pedido Comanda - visível para todos, se habilitado nos parâmetros da empresa.
                Seleciona a mesa (se a empresa pedir) e depois a comanda, mesmo que a empresa não exija comanda no Pedido Mesa. */}
            {showComandaOrder && (
                <button className={"shorcut-button"} onClick={() => {
                    localStorage.removeItem('sale');
                    navigateWithSessionCheck('/sale/table-comanda');
                }}>Pedido Comanda
                </button>
            )}

            {/* Pedido Take Away - visível para todos, se habilitado nos parâmetros da empresa */}
            {showTakeAwayOrder && (
                <button className={"shorcut-button"} onClick={() => {
                    localStorage.removeItem('sale');
                    navigateWithSessionCheck('/sale/takeaway');
                }}>Pedido Take Away
                </button>
            )}

            {/* Pedido Balcão - para INTERMEDIATE e ADMIN, se habilitado nos parâmetros da empresa */}
            {(isIntermediateUser || isAdminUser) && showCounterOrder && (
                <button className={"shorcut-button"} onClick={() => {
                    localStorage.removeItem('sale');
                    navigateWithSessionCheck('/sale/items');
                }}>Pedido Balcão
                </button>
            )}

            {/* Pedido Delivery - para INTERMEDIATE e ADMIN, só se a empresa tiver entrega habilitada.
                Usa o mesmo mecanismo do Take Away: pede telefone e nome antes dos itens. */}
            {(isIntermediateUser || isAdminUser) && deliveryEnabled && (
                <button className={"shorcut-button"} onClick={() => {
                    localStorage.removeItem('sale');
                    navigateWithSessionCheck('/sale/delivery-customer');
                }}>Pedido Delivery
                </button>
            )}

            {/* Fechar Pedidos - para INTERMEDIATE e ADMIN: tudo o que está em aberto (mesa, comanda, take away) */}
            {(isIntermediateUser || isAdminUser) && (
                <button className={"shorcut-button shorcut-button-close"} onClick={() => {
                    localStorage.removeItem('sale');
                    navigate('/sale/orders-pending');
                }}>Fechar Pedidos
                </button>
            )}
            
            {/* Reimprimir - somente ADMIN */}
            {isAdminUser && (
                <button className={"shorcut-button shorcut-button-secondary"} onClick={() => {
                    navigate('/sale/print');
                }}>Reimprimir
                </button>
            )}
            
            {/* Consultar Pedidos - para INTERMEDIATE e ADMIN */}
            {(isIntermediateUser || isAdminUser) && (
                <button className={"shorcut-button shorcut-button-secondary"} onClick={() => {
                    navigate('/sale/sales');
                }}>Consultar Pedidos
                </button>
            )}

            {/* Convite por QR — intermediário e admin */}
            {(isIntermediateUser || isAdminUser) && showWaiterQrCode && (
                <button
                    className={"shorcut-button shorcut-button-secondary"}
                    style={{
                        whiteSpace: 'nowrap',
                    }}
                    type="button"
                    onClick={openQrModal}
                >
                    Garçom · QR Code
                </button>
            )}

            {qrModalOpen && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.5)',
                        zIndex: 2000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 8,
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                    }}
                    onClick={closeQrModal}
                >
                    <div
                        style={{
                            background: '#fff',
                            width: '100%',
                            maxWidth: QR_MODAL_MAX_PX,
                            minWidth: 0,
                            maxHeight: '100%',
                            boxSizing: 'border-box',
                            padding: '10px 12px 12px',
                            borderRadius: 10,
                            boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
                            display: 'flex',
                            flexDirection: 'column',
                            overflowX: 'hidden',
                            overflowY: 'auto',
                            WebkitOverflowScrolling: 'touch',
                            position: 'relative',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            onClick={closeQrModal}
                            aria-label="Fechar"
                            style={{
                                position: 'absolute',
                                top: 8,
                                right: 10,
                                border: 'none',
                                background: 'transparent',
                                fontSize: 22,
                                lineHeight: 1,
                                cursor: 'pointer',
                                color: '#666',
                                padding: 2,
                            }}
                        >
                            ×
                        </button>
                        <div style={{fontWeight: 700, fontSize: 16, marginBottom: 6, flexShrink: 0}}>
                            {qrResult ? 'QR Code — login' : 'Garçom · QR Code'}
                        </div>
                        {!qrResult && (
                            <p style={{
                                fontSize: 12,
                                color: '#555',
                                margin: '0 0 8px',
                                lineHeight: 1.35,
                                overflowWrap: 'anywhere',
                                wordBreak: 'break-word',
                                maxWidth: '100%',
                            }}
                            >
                                Nome em letras maiúsculas (pode incluir números) · 1 acesso · 48h
                            </p>
                        )}
                        {!qrResult && (
                            <>
                                <input
                                    type="text"
                                    inputMode="text"
                                    autoComplete="name"
                                    style={{
                                        width: '100%',
                                        maxWidth: '100%',
                                        minWidth: 0,
                                        boxSizing: 'border-box',
                                        padding: '14px 12px',
                                        fontSize: 16,
                                        marginBottom: 8,
                                        borderRadius: 8,
                                        border: '1px solid #ccc',
                                    }}
                                    value={qrInviteName}
                                    onChange={(e) => setQrInviteName(sanitizeQrUserNameInput(e.target.value))}
                                    disabled={qrLoading}
                                    placeholder="NOME (LETRAS E NÚMEROS)"
                                    spellCheck={false}
                                    autoCapitalize="characters"
                                />
                                <select
                                    style={{
                                        width: '100%',
                                        maxWidth: '100%',
                                        minWidth: 0,
                                        boxSizing: 'border-box',
                                        padding: '14px 12px',
                                        fontSize: 16,
                                        marginBottom: 8,
                                        borderRadius: 8,
                                        border: '1px solid #ccc',
                                        background: '#fff',
                                    }}
                                    value={qrInviteUserType}
                                    onChange={(e) => setQrInviteUserType(parseInt(e.target.value, 10))}
                                    disabled={qrLoading}
                                >
                                    {allowedQrUserTypeOptions.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            </>
                        )}
                        {qrError && (
                            <p style={{color: '#c00', fontSize: 13, margin: '0 0 8px'}}>{qrError}</p>
                        )}
                        {qrResult && (
                            <div
                                style={{
                                    textAlign: 'center',
                                    flex: 1,
                                    minHeight: 0,
                                    minWidth: 0,
                                    width: '100%',
                                    maxWidth: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                }}
                            >
                                <p style={{fontSize: 13, margin: '0 0 6px', flexShrink: 0}}>
                                    <strong>{qrResult.userName}</strong>
                                </p>
                                <p style={{fontSize: 11, color: '#666', margin: '0 0 8px', flexShrink: 0}}>
                                    Escaneie com a câmera do celular
                                </p>
                                <img
                                    alt="QR Code de login"
                                    style={{
                                        width: '100%',
                                        maxWidth: '100%',
                                        minWidth: 0,
                                        height: 'auto',
                                        display: 'block',
                                        objectFit: 'contain',
                                        verticalAlign: 'top',
                                    }}
                                    src={`data:image/png;base64,${qrResult.qrCodePngBase64}`}
                                />
                                <p
                                    title={qrResult.loginUrl}
                                    style={{
                                        fontSize: 10,
                                        color: '#666',
                                        margin: '6px 0 0',
                                        width: '100%',
                                        maxWidth: QR_MODAL_MAX_PX,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {qrResult.loginUrl}
                                </p>
                            </div>
                        )}
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 8,
                                marginTop: 10,
                                flexShrink: 0,
                            }}
                        >
                            {!qrResult && (
                                <button
                                    type="button"
                                    onClick={submitQrInvite}
                                    disabled={qrLoading}
                                    style={{
                                        width: '100%',
                                        maxWidth: '100%',
                                        minWidth: 0,
                                        margin: 0,
                                        padding: '14px 12px',
                                        fontSize: 16,
                                        borderRadius: 8,
                                        border: 'none',
                                        boxSizing: 'border-box',
                                    }}
                                >
                                    {qrLoading ? 'Gerando…' : 'Gerar QR'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Shortcuts;
